/* =========================================================
   Scroll background per section (Baseline + Lenis)
   - règle: change quand le haut de la section franchit une ligne à 30% du bas
   - fix: les sections "vides" (white space) ont un scheme mais un bg transparent
          => fallback sur --color-scheme-background
   ========================================================= */

(() => {
  const root = document.documentElement;

  function getStickyHeaderHeightPx() {
    const v = getComputedStyle(root).getPropertyValue('--sticky-header-height').trim();
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }

  function isTransparent(bg) {
    return !bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)';
  }

  // Baseline: récupère la couleur de fond du scheme (fonctionne même si la section est "vide")
  // (le scheme est généralement porté par un élément avec data-color-scheme)
  function schemeBgColor(node) {
    if (!node) return null;

    const host =
      (node.matches && node.matches('[data-color-scheme]')) ? node :
      (node.closest ? node.closest('[data-color-scheme]') : null);

    if (!host) return null;

    const raw = getComputedStyle(host).getPropertyValue('--color-scheme-background').trim();
    if (!raw) return null;

    // Baseline stocke souvent "R G B"
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length < 3) return null;

    return `rgb(${parts[0]} ${parts[1]} ${parts[2]})`;
  }

  // Trouve le "bloc visuel" principal de la section (celui qui porte le fond)
  function findSectionVisualBlock(shopifySection) {
    const direct = shopifySection.querySelector(':scope > *');
    if (!direct) return shopifySection;

    if (direct.classList && direct.classList.contains('bg-scheme-background')) return direct;

    const inner = direct.querySelector(':scope > .bg-scheme-background');
    if (inner) return inner;

    const deep = shopifySection.querySelector('.bg-scheme-background');
    if (deep) return deep;

    return direct;
  }

  // Calcule la couleur de fond réelle (avant qu'on ne rende transparent)
  function computeBgColor(el) {
    if (!el) return null;

    let bg = getComputedStyle(el).backgroundColor;
    if (!isTransparent(bg)) return bg;

    let p = el.parentElement;
    let depth = 0;
    while (p && depth < 3) {
      bg = getComputedStyle(p).backgroundColor;
      if (!isTransparent(bg)) return bg;
      p = p.parentElement;
      depth++;
    }
    return null;
  }

  function apply(color) {
    if (!color) return;
    root.style.setProperty('--scroll-bg', color);
  }

  function init() {
    const main = document.querySelector('#MainContent');
    if (!main) return;

    const shopifySections = Array.from(main.querySelectorAll(':scope > .shopify-section'));
    if (!shopifySections.length) return;

    // 0) Fallback initial : couleur actuelle du thème
    const initialBg = getComputedStyle(root).backgroundColor;
    if (!isTransparent(initialBg)) apply(initialBg);

    // 1) Pré-calculer couleurs + cibles
    const sectionColor = new Map();
    const sectionTarget = new Map();

    for (const s of shopifySections) {
      const visual = findSectionVisualBlock(s);

      // ✅ Nouvelle stratégie de couleur:
      // - on tente d'abord la couleur réellement peinte
      // - si transparent (white space), on fallback sur la couleur de scheme Baseline
      const color =
        computeBgColor(visual) ||
        schemeBgColor(visual) ||
        computeBgColor(s) ||
        schemeBgColor(s);

      if (color) sectionColor.set(s, color);
      sectionTarget.set(s, visual);
    }

    // 2) Activer le mode
    root.classList.add('scroll-bg-enabled');
    document.body.classList.add('scroll-bg-enabled');

    // 3) Rendre transparents les blocs visuels
    for (const s of shopifySections) {
      const target = sectionTarget.get(s);
      if (target) target.setAttribute('data-scroll-bg-target', '');
    }

    // 4) Nouvelle logique: ligne repère à 30% du bas
    // => y = 70% du viewport depuis le haut
    const headerH = getStickyHeaderHeightPx();
    const triggerYRatioFromTop = 0.70;

    let ticking = false;
    let active = null;

    function pickActiveSection() {
      const y = Math.round(window.innerHeight * triggerYRatioFromTop);
      // Optionnel: si le header "couvre" visuellement, on remonte un peu la ligne
      const adjustedY = Math.max(0, y + Math.round(headerH * 0.2));

      for (const s of shopifySections) {
        const r = s.getBoundingClientRect();
        if (r.top <= adjustedY && r.bottom > adjustedY) return s;
      }

      return shopifySections[0];
    }

    function update() {
      ticking = false;

      const next = pickActiveSection();
      if (next && next !== active) {
        active = next;
        const color = sectionColor.get(active);
        if (color) apply(color);
      }
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    // init immédiat
    active = pickActiveSection();
    apply(sectionColor.get(active) || sectionColor.get(shopifySections[0]));
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();
